import { useCallback, useLayoutEffect, useRef } from 'react';

import type {
  ClipRect,
  DrawImageInstruction,
  DrawLineInstruction,
  DrawRectInstruction,
  DrawTextInstruction,
  TransformationMatrix,
} from '@aline/common';
import { assertNeverSafely } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';

import { getOrLoadCanvasImage } from './canvas-image-cache';
import { getIsDrawableHtmlImage } from './get-is-drawable-html-image';
import type { AssetMap } from './use-assets';
import { useAssets } from './use-assets';
import type { CanvasDrawInstruction } from './use-page-draw-instructions';
import { alineFontString } from '../../../../common/src/slate-api/render-model/textmetrics';

export function usePageCanvas(
  drawInstructions: CanvasDrawInstruction[],
  options: {
    pageHeightPx: number;
    pageWidthPx: number;
  }
) {
  const client = useApolloClient();
  const { pageHeightPx, pageWidthPx } = options;
  const assetMap = useAssets(drawInstructions);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const refreshedImageIdsRef = useRef(new Set<string>());

  /**
   * Signed Filestack asset URLs expire after their policy window, so an image that sat in the
   * Apollo cache can fail to load. Refetch the file once (network-only) to mint a fresh signed
   * URL; the cache write updates useAssets, which re-renders and redraws with the new URL.
   */
  const handleImageLoadError = useCallback(
    (imageId: string) => {
      if (refreshedImageIdsRef.current.has(imageId)) return;
      refreshedImageIdsRef.current.add(imageId);

      void client
        .query<Apollo.GetFilesQuery, Apollo.GetFilesQueryVariables>({
          fetchPolicy: 'network-only',
          query: Apollo.GetFilesDocument,
          variables: { fileIds: [imageId] },
        })
        .catch(() => null);
    },
    [client]
  );

  useLayoutEffect(() => {
    function draw() {
      const { current: canvas } = canvasRef;
      if (!canvas) return;

      const canvasRenderer = new CanvasRenderer({
        assetMap,
        canvas,
        height: pageHeightPx,
        onImageLoadError: handleImageLoadError,
        onRedraw: draw,
        scaleFactor: window.devicePixelRatio * (window.visualViewport?.scale ?? 1),
        width: pageWidthPx,
      });

      for (const drawInstruction of drawInstructions) {
        canvasRenderer.draw(drawInstruction);
      }
    }

    draw();
    window.visualViewport?.addEventListener('resize', draw);
    return () => window.visualViewport?.removeEventListener('resize', draw);
  });

  return { canvasRef };
}

/**
 * Short-lived object that groups together helper methods for rendering draw instructions to the
 * canvas.
 */
class CanvasRenderer {
  private ctx: CanvasRenderingContext2D;
  private scaleFactor: number;
  private assetMap: AssetMap;
  private onImageLoadError: (imageId: string) => void;
  private onRedraw: () => void;

  constructor({
    assetMap,
    canvas,
    height,
    onImageLoadError,
    onRedraw,
    scaleFactor,
    width,
  }: {
    assetMap: AssetMap;
    canvas: HTMLCanvasElement;
    height: number;
    onImageLoadError: (imageId: string) => void;
    onRedraw: () => void;
    scaleFactor: number;
    width: number;
  }) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get rendering context');
    this.ctx = ctx;
    this.scaleFactor = scaleFactor;
    this.assetMap = assetMap;
    this.onImageLoadError = onImageLoadError;
    this.onRedraw = onRedraw;

    const deviceWidth = width * scaleFactor;
    const deviceHeight = height * scaleFactor;

    // PERF: Only update the canvas size (a slow operation) when its size or scale factor changes
    if (canvas.width !== deviceWidth || canvas.height !== deviceHeight) {
      canvas.width = deviceWidth;
      canvas.height = deviceHeight;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    ctx.clearRect(0, 0, deviceWidth, deviceHeight);
  }

  draw(drawInstruction: CanvasDrawInstruction) {
    switch (drawInstruction.type) {
      case 'text':
        this.drawText(drawInstruction);
        break;

      case 'rect':
        this.drawRect(drawInstruction);
        break;

      case 'line':
        this.drawLine(drawInstruction);
        break;

      case 'image':
        this.drawImage(drawInstruction);
        break;

      default:
        assertNeverSafely(drawInstruction);
    }
  }

  // Draw methods

  private drawText({ clipRect, color, font, text, x, y }: DrawTextInstruction) {
    this.saveAndRestore(() => {
      this.applyClipRect(clipRect);
      this.ctx.scale(this.scaleFactor, this.scaleFactor);
      this.ctx.font = alineFontString(font);
      this.ctx.fillStyle = color;
      this.ctx.fillText(text, x, y);
    });
  }

  private drawRect({
    clipRect,
    cornerRadius = 0,
    fillColor,
    height,
    stroke,
    width,
    x,
    y,
  }: DrawRectInstruction) {
    /**
     * It's possible for scale(x, 'round') + scale(width, 'round') to differ slightly from
     * scale(x + width, 'round') due to rounding. To ensure that to adjacent rectangles are drawn
     * with no visible gap, we use the latter to determine the actual drawn width and height
     */
    const scaledStartX = this.scale(x, 'round');
    const scaledStartY = this.scale(y, 'round');
    const scaledEndX = this.scale(x + width, 'round');
    const scaledEndY = this.scale(y + height, 'round');
    const scaledWidth = scaledEndX - scaledStartX;
    const scaledHeight = scaledEndY - scaledStartY;

    const { offsetDueToThickness = 0, scaledThickness = 0 } = stroke
      ? this.scaleLineThickness(stroke.thickness)
      : {};

    this.saveAndRestore(() => {
      this.applyClipRect(clipRect);

      if (fillColor) this.ctx.fillStyle = fillColor;

      if (stroke) {
        this.ctx.lineWidth = scaledThickness;
        this.ctx.strokeStyle = stroke.color;
      }

      this.ctx.beginPath();

      this.ctx.roundRect(
        scaledStartX + offsetDueToThickness,
        scaledStartY + offsetDueToThickness,
        scaledWidth,
        scaledHeight,
        this.scale(cornerRadius)
      );

      if (fillColor) this.ctx.fill();
      if (stroke) this.ctx.stroke();
    });
  }

  private drawLine({
    clipRect,
    color,
    dashPattern,
    direction,
    endX,
    endY,
    lineCap,
    startX,
    startY,
    thickness,
    transform,
  }: DrawLineInstruction) {
    const { offsetDueToThickness, scaledThickness } = this.scaleLineThickness(thickness);

    this.saveAndRestore(() => {
      this.applyTransform(transform);
      this.applyClipRect(clipRect);

      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = scaledThickness;
      if (lineCap) this.ctx.lineCap = lineCap;

      if (dashPattern) {
        this.ctx.setLineDash(dashPattern.map(value => this.scale(value, 'round')));
      }

      this.ctx.beginPath();
      this.ctx.moveTo(
        this.scale(startX, 'round') + (direction === 'vertical' ? offsetDueToThickness : 0),
        this.scale(startY, 'round') + (direction === 'horizontal' ? offsetDueToThickness : 0)
      );
      this.ctx.lineTo(
        this.scale(endX, 'round') + (direction === 'vertical' ? offsetDueToThickness : 0),
        this.scale(endY, 'round') + (direction === 'horizontal' ? offsetDueToThickness : 0)
      );
      this.ctx.stroke();
    });
  }

  private drawImage({
    clipRect,
    height,
    imageId,
    opacity = 1,
    transform,
    width,
    x,
    y,
  }: DrawImageInstruction) {
    /**
     * Try to fetch the image from the asset map. If the asset map hasn't loaded yet, React will re-
     * render the component and trigger a redraw when it loads.
     */
    const { filestackUrl } = this.assetMap.get(imageId) ?? {};
    const cachedImage = getOrLoadCanvasImage({
      imageId,
      onError: this.onImageLoadError,
      onLoad: this.onRedraw,
      url: filestackUrl,
    });

    this.saveAndRestore(() => {
      this.applyClipRect(clipRect);
      this.applyTransform(transform);
      this.ctx.globalAlpha = opacity;

      if (!cachedImage || !getIsDrawableHtmlImage(cachedImage)) {
        this.drawImagePlaceholder(x, y, width, height);
        return;
      }

      this.ctx.drawImage(
        cachedImage,
        this.scale(x, 'round'),
        this.scale(y, 'round'),
        this.scale(width, 'round'),
        this.scale(height, 'round')
      );
    });
  }

  // Helpers

  private saveAndRestore(fn: () => void) {
    this.ctx.save();
    fn();
    this.ctx.restore();
  }

  /**
   * Scale a CSS pixel value by the current scaling factor to produce a screen pixel value,
   * optionally rounded to prevent antialiasing.
   */
  private scale(value: number, rounding?: 'round' | 'ceil' | 'floor') {
    const scaledValue = value * this.scaleFactor;
    if (rounding) return Math[rounding](scaledValue);
    return scaledValue;
  }

  /**
   * Apply a transformation matrix to the canvas. Since the transformation matrix is defined using
   * CSS pixels whereas we're drawing to the canvas in screen pixels, we need to scale the e and f
   * components of the matrix. These components provide horizontal and vertical translation
   * respectively. The other components don't require scaling since the browser will multiply them
   * by the already scaled x and y coordinates.
   */
  private applyTransform(transform: TransformationMatrix | undefined) {
    if (!transform) return;
    const [a, b, c, d, e, f] = transform;
    this.ctx.transform(a, b, c, d, this.scale(e), this.scale(f));
  }

  private scaleLineThickness(thickness: number): {
    offsetDueToThickness: number;
    scaledThickness: number;
  } {
    /**
     * Ceil the line width to a screen pixel, with a minimum thickness of one CSS pixel, ensuring
     * that lines are sharp and clearly visible at all zoom levels. This matches how lines such as
     * underlines, strikethroughs and table borders are rendered in Word.
     */
    const scaledThickness = Math.max(this.scale(thickness, 'ceil'), window.devicePixelRatio);

    /**
     * If the thickness is an odd number of screen pixels, translate the line by 0.5 screen pixels
     * in the direction perpendicular to the line. This ensures that the long sides of the line are
     * exactly aligned with the screen pixel grid, preventing antialiasing.
     */
    const offsetDueToThickness = scaledThickness % 2 === 1 ? 0.5 : 0;

    return { offsetDueToThickness, scaledThickness };
  }

  private applyClipRect(clipRect: ClipRect | null) {
    if (!clipRect) return;
    const { height, width, x, y } = clipRect;
    this.ctx.beginPath();
    this.ctx.rect(
      this.scale(x, 'round'),
      this.scale(y, 'round'),
      this.scale(width, 'round'),
      this.scale(height, 'round')
    );
    this.ctx.clip();
  }

  private drawImagePlaceholder(x: number, y: number, width: number, height: number) {
    // Background
    this.saveAndRestore(() => {
      this.ctx.fillStyle = '#f5f5f5';
      this.ctx.beginPath();
      this.ctx.rect(
        this.scale(x, 'round'),
        this.scale(y, 'round'),
        this.scale(width, 'round'),
        this.scale(height, 'round')
      );
      this.ctx.fill();
    });

    // Icon
    this.saveAndRestore(() => {
      const baseIconSize = 24;
      const iconSize = Math.min(width, height) * 0.4;
      const iconScaleFactor = iconSize / baseIconSize;

      this.ctx.scale(this.scaleFactor, this.scaleFactor);
      this.ctx.translate(x + width / 2, y + height / 2);
      this.ctx.scale(iconScaleFactor, iconScaleFactor);
      this.ctx.translate(-baseIconSize / 2, -baseIconSize / 2);

      this.ctx.lineWidth = 1;
      this.ctx.strokeStyle = '#bdbdbd';
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';

      this.ctx.stroke(new Path2D('M4,3 L15,3 L18,6 L18,17 L4,17 Z'));
      this.ctx.stroke(new Path2D('M6,14 L8,11 L10,13 L12,10 L15,14'));

      this.ctx.beginPath();
      this.ctx.ellipse(8, 7, 1.5, 1.5, 0, 0, 2 * Math.PI);
      this.ctx.stroke();
    });
  }
}
